// Archivio (tela, artboard 26 e 37): le sessioni chiuse, concluse, fallite o annullate, e i
// progetti archiviati. Le sessioni aperte stanno in Dashboard.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { Project } from '../../lib/api';
import { RelativeTime } from '../../components/RelativeTime';
import { Segmented } from '../../components/Segmented';
import type { Session } from '../../types';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ConversationRow } from '../../components/ConversationRow';
import { EmptyState } from '../../components/EmptyState';
import { FilterBar } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { Select } from '../../components/Select';
import { Skeleton, SkeletonRow, SkeletonStat } from '../../components/Skeleton';
import { KpiTile } from '../control/KpiTile';
import { useStore } from '../../store/StoreProvider';
import { closedSessions, projects, sortedSessions } from '../../store/selectors';
import { relative } from '../../lib/time';

const CLOSED: { state: Session['state']; label: string; plural: string }[] = [
  { state: 'completed', label: 'Conclusa', plural: 'Concluse' },
  { state: 'failed', label: 'Fallita', plural: 'Fallite' },
  { state: 'cancelled', label: 'Annullata', plural: 'Annullate' },
];

function ArchiveSessions() {
  const { state, dispatch, reload } = useStore();
  const nav = useNavigate();
  const closed = closedSessions(state);
  const closedIds = new Set(closed.map((s) => s.id));
  const list = sortedSessions(state).filter((s) => closedIds.has(s.id));
  const f = state.filters;
  const reset = () =>
    dispatch({
      type: 'filters/set',
      patch: { search: '', state: '', project: '', onlyWaiting: false },
    });

  const previewOf = (id: string) => {
    const msgs = state.messages.filter((m) => m.sessionId === id);
    return msgs[msgs.length - 1]?.text ?? '';
  };

  // lo scheletro solo al primo caricamento: un refresh (polling, richiesta inviata) non deve
  // togliere il focus alla ricerca né chiudere una select aperta
  const first = state.sessions.length === 0;
  if (state.load === 'idle' || (state.load === 'loading' && first)) {
    return (
      <div className="page">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Skeleton w={200} h={28} />
        </div>
        <Card style={{ display: 'flex', gap: 20, marginBottom: 16 }} compact>
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      </div>
    );
  }

  if (state.load === 'error' && first) {
    return (
      <div className="page">
        <PageHeader title="Archivio" />
        <Card accent="var(--danger)">
          <div className="t-h3" style={{ marginBottom: 6 }}>
            Impossibile contattare poptown-api.
          </div>
          <div className="t-mono-xs dim" style={{ marginBottom: 10 }}>
            {state.loadError}
          </div>
          <Button onClick={reload}>Riprova</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Archivio"
        subtitle="Le sessioni chiuse: concluse, fallite o annullate. Quelle aperte stanno in Dashboard."
      />

      {state.degraded && (
        <div style={{ marginBottom: 16 }}>
          <Banner kind="warn">
            Aggiornamenti in tempo reale non disponibili: sto aggiornando ogni 15 secondi.
          </Banner>
        </div>
      )}

      <div
        className="dash-kpis"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 16 }}
      >
        {CLOSED.map((c, i) => {
          const these = closed.filter((s) => s.state === c.state);
          const last = these.reduce<string | null>(
            (at, s) => (at === null || s.updatedAt > at ? s.updatedAt : at),
            null,
          );
          return (
            <KpiTile
              key={c.state}
              label={c.plural}
              value={these.length}
              note={last ? `ultima ${relative(last)}` : 'nessuna'}
              pop={['qa-01', 'devops-01', 'writer-01'][i % 3]}
              onClick={() => dispatch({ type: 'filters/set', patch: { state: c.state } })}
            />
          );
        })}
      </div>

      <div style={{ marginBottom: 12 }}>
        <FilterBar
          search={f.search}
          onSearch={(v) => dispatch({ type: 'filters/set', patch: { search: v } })}
          onClear={f.search || f.state || f.project || f.onlyWaiting ? reset : undefined}
        >
          <Select
            value={f.state}
            onChange={(v) => dispatch({ type: 'filters/set', patch: { state: v } })}
            options={[
              { value: '', label: 'Stato' },
              ...CLOSED.map((c) => ({ value: c.state, label: c.label })),
            ]}
            label="Filtra per stato"
            width={150}
          />
          <Select
            value={f.project}
            onChange={(v) => dispatch({ type: 'filters/set', patch: { project: v } })}
            options={[
              { value: '', label: 'Progetto' },
              ...projects(state).map((p) => ({ value: p.id, label: p.name })),
            ]}
            label="Filtra per progetto"
            width={160}
          />
        </FilterBar>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((s) => (
          <ConversationRow
            key={s.id}
            s={s}
            preview={previewOf(s.id)}
            onOpen={() => nav('/s/' + s.id)}
          />
        ))}
      </div>

      {list.length === 0 && (
        <Card>
          {closed.length === 0 ? (
            <EmptyState
              pop="writer-01"
              icon="box"
              title="L’archivio è vuoto."
              hint="Quando una sessione si chiude, conclusa, fallita o annullata, arriva qui."
            />
          ) : (
            <EmptyState
              icon="filter"
              title="Nessuna sessione con questi filtri."
              hint="Azzera i filtri per rivedere tutto l’archivio."
              action={<Button onClick={reset}>Azzera filtri</Button>}
            />
          )}
        </Card>
      )}
    </div>
  );
}

// Archivio (S7; tela, artboard 37): le sessioni chiuse e i progetti archiviati, con «Ripristina».
export function Archive() {
  const [tab, setTab] = useState<'sessions' | 'projects'>('sessions');
  return (
    <>
      <div className="page" style={{ paddingBottom: 0 }}>
        <Segmented
          label="Cosa mostra l'archivio"
          value={tab}
          items={[
            { value: 'sessions', label: 'Sessioni' },
            { value: 'projects', label: 'Progetti' },
          ]}
          onChange={setTab}
        />
      </div>
      {tab === 'sessions' ? <ArchiveSessions /> : <ArchivedProjects />}
    </>
  );
}

function ArchivedProjects() {
  const { state, toast } = useStore();
  const [rows, setRows] = useState<Project[] | null>(null);
  const load = () =>
    void api.projects().then(
      (r) => setRows(r.projects.filter((p) => p.archived)),
      () => setRows([]),
    );
  useEffect(load, []);
  const restore = async (p: Project) => {
    try {
      await api.archiveProject(p.id, false);
      toast({ kind: 'success', title: `${p.name}: di nuovo fra i progetti` });
      load();
    } catch {
      toast({ kind: 'danger', title: 'Non ripristinato', body: 'Riprova fra poco.' });
    }
  };
  return (
    <div className="page">
      <PageHeader
        title="Archivio"
        subtitle="I progetti archiviati: restano com'erano, si ripristinano quando vuoi."
      />
      {rows === null ? (
        <SkeletonRow />
      ) : rows.length === 0 ? (
        <div className="t-caption faint">Nessun progetto archiviato.</div>
      ) : (
        <section className="np-card">
          {rows.map((p) => (
            <div key={p.id} className="np-row">
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="t-strong" style={{ display: 'block' }}>
                  {p.name}
                </span>
                <span className="t-caption dim">
                  {p.archivedAt ? (
                    <>
                      archiviato <RelativeTime at={p.archivedAt} />
                    </>
                  ) : (
                    'archiviato'
                  )}{' '}
                  · {state.sessions.filter((s) => s.project === p.id).length} sessioni
                </span>
              </span>
              <Button size="sm" onClick={() => void restore(p)}>
                Ripristina
              </Button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
